import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { selectRoomSession, selectUsersWithClientRole } from '../../../../../../store/meetingSummary/roomSession'
import { selectTasks } from '../../../../../../store/task/task'
import { openModal } from '../../../../../../store/modal/modal'
import { setUpdateStartedFrom } from '../../../../../../store/meetingSummary/contactUpdate'
import { useGetMeQuery } from '../../../../../../store/user/user'
import { type ToolUse } from '../../../../../../models/StreamingAIAgent'
import { AIAgentType } from '../../../../../../models/AIAgent'
import { getIntegrationDisplayName, type IntegrationServiceId } from '../../../../../../common/integrations'
import { useIntegrations } from '../../../../../../views/Integration/useIntegrations'
import { getUserActionPayload } from '../../../../../../views/AIAgent/UserActions/model'
import { IntegrationUserAction } from '../../../../../../views/AIAgent/UserActions/IntegrationUserAction'
import { IntegrationUserActionButton } from '../../../../../../views/AIAgent/UserActions/IntegrationUserActionButton'
import { useTrackUserActionButtonClick } from '../../../../../../views/AIAgent/UserActions/useTrackUserActionButtonClick'
import { getUserDisplayName } from '../../../common/aifeatures/utils'

interface MeetingSummaryChatToolUseProps {
  toolUse: ToolUse
}

export const MeetingSummaryChatToolUse: React.FC<MeetingSummaryChatToolUseProps> = ({ toolUse }) => {
  switch (toolUse.name) {
    case 'offer_user_action':
      return <MeetingSummaryChatUserAction toolUse={toolUse} />
    default:
      return null
  }
}

const MeetingSummaryChatUserAction: React.FC<MeetingSummaryChatToolUseProps> = ({ toolUse }) => {
  const userAction = getUserActionPayload(toolUse)

  switch (userAction?.type) {
    case 'save_meeting_summary':
      return <MeetingSummaryChatSaveSummaryUserAction serviceId={userAction.integration_service_id} />
    case 'save_tasks':
      return <MeetingSummaryChatSaveTasksUserAction serviceId={userAction.integration_service_id} />
    case 'save_contact_details':
      return <MeetingSummaryChatSaveContactDetailsUserAction serviceId={userAction.integration_service_id} contactId={userAction.contact_id} />
    case 'save_summary_as_pdf_document':
      return <MeetingSummaryChatSaveSummaryAsPdfUserAction serviceId={userAction.integration_service_id} />
    default:
      return null
  }
}

interface MeetingSummaryChatIntegrationUserActionProps {
  serviceId: IntegrationServiceId
}

const MeetingSummaryChatSaveSummaryUserAction: React.FC<MeetingSummaryChatIntegrationUserActionProps> = ({ serviceId }) => {
  const dispatch = useAppDispatch()
  const roomSession = useAppSelector(selectRoomSession)
  const { supportsMeetingSummaryExport } = useIntegrations()

  const trackButtonClick = useTrackUserActionButtonClick({ agentType: AIAgentType.MEETING_SUMMARY, actionType: 'save_meeting_summary', serviceId })

  const handleSaveSummaryClick = () => {
    if (!roomSession) {
      return
    }

    trackButtonClick('save')
    dispatch(openModal({ type: 'exportMeetingSummary', serviceId, roomSession }))
  }

  return (
    <IntegrationUserAction serviceId={serviceId} isSupported={!!roomSession && supportsMeetingSummaryExport(serviceId)} onConnectionClick={trackButtonClick}>
      <IntegrationUserActionButton
        serviceId={serviceId}
        label={`Save meeting summary to ${getIntegrationDisplayName(serviceId)}`}
        onClick={handleSaveSummaryClick}
      />
    </IntegrationUserAction>
  )
}

const MeetingSummaryChatSaveTasksUserAction: React.FC<MeetingSummaryChatIntegrationUserActionProps> = ({ serviceId }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const roomSession = useAppSelector(selectRoomSession)
  const tasks = useAppSelector(selectTasks)

  const { data: currentUser } = useGetMeQuery({ accountId })
  const { supportsTaskExport } = useIntegrations()

  const externalIntegration = currentUser?.integrations?.find((integration) => integration.serviceId === serviceId)

  const trackButtonClick = useTrackUserActionButtonClick({ agentType: AIAgentType.MEETING_SUMMARY, actionType: 'save_tasks', serviceId })

  const handleSaveTasksClick = () => {
    if (!roomSession || !tasks || !externalIntegration) {
      return
    }

    trackButtonClick('save')
    dispatch(openModal({ type: 'createTask', serviceId, roomSession, tasks, externalIntegration }))
  }

  return (
    <IntegrationUserAction serviceId={serviceId} isSupported={!!roomSession && supportsTaskExport(serviceId)} onConnectionClick={trackButtonClick}>
      {tasks?.length ? (
        <IntegrationUserActionButton serviceId={serviceId} label={`Save tasks to ${getIntegrationDisplayName(serviceId)}`} onClick={handleSaveTasksClick} />
      ) : null}
    </IntegrationUserAction>
  )
}

interface MeetingSummaryChatSaveContactDetailsUserActionProps extends MeetingSummaryChatIntegrationUserActionProps {
  contactId: string
}

const MeetingSummaryChatSaveContactDetailsUserAction: React.FC<MeetingSummaryChatSaveContactDetailsUserActionProps> = ({ serviceId, contactId }) => {
  const dispatch = useAppDispatch()
  const clientUsers = useAppSelector(selectUsersWithClientRole)

  const { canCreateOrUpdateContact } = useIntegrations()

  const trackButtonClick = useTrackUserActionButtonClick({ agentType: AIAgentType.MEETING_SUMMARY, actionType: 'save_contact_details', serviceId })

  const contact = clientUsers.find((user) => user.id === contactId && user.type === 'external')

  if (!contact) {
    return null
  }

  const isSupported = canCreateOrUpdateContact(serviceId, contact)

  const handleSaveContactDetailsClick = () => {
    trackButtonClick('save')
    dispatch(setUpdateStartedFrom('meeting'))
    dispatch(openModal({ type: 'updateContact', serviceId, user: contact }))
  }

  return (
    <IntegrationUserAction serviceId={serviceId} isSupported={isSupported} onConnectionClick={trackButtonClick}>
      <IntegrationUserActionButton
        serviceId={serviceId}
        label={`Save contact details to ${getIntegrationDisplayName(serviceId)} (${getUserDisplayName(contact)})`}
        onClick={handleSaveContactDetailsClick}
      />
    </IntegrationUserAction>
  )
}

const MeetingSummaryChatSaveSummaryAsPdfUserAction: React.FC<MeetingSummaryChatIntegrationUserActionProps> = ({ serviceId }) => {
  const dispatch = useAppDispatch()
  const { supportsDocumentUpload } = useIntegrations()

  const trackButtonClick = useTrackUserActionButtonClick({
    agentType: AIAgentType.MEETING_SUMMARY,
    actionType: 'save_summary_as_pdf_document',
    serviceId,
  })

  const handleSaveSummaryAsPdfClick = () => {
    trackButtonClick('save')
    dispatch(openModal({ type: 'saveMeetingSummaryToDocumentConnector', serviceId }))
  }

  return (
    <IntegrationUserAction serviceId={serviceId} isSupported={supportsDocumentUpload(serviceId)} onConnectionClick={trackButtonClick}>
      <IntegrationUserActionButton
        serviceId={serviceId}
        label={`Save summary as PDF to ${getIntegrationDisplayName(serviceId)}`}
        onClick={handleSaveSummaryAsPdfClick}
      />
    </IntegrationUserAction>
  )
}
